import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Plus,
  Users,
  Loader2,
  CheckCircle,
  AlertCircle,
  DollarSign,
  TrendingUp,
  User,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SessionUpgrade({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  
  // Booking modal state
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [bookingType, setBookingType] = useState("remaining"); // "single", "remaining", "full", or "term_with_next"
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [calculatingFee, setCalculatingFee] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [bookingOptions, setBookingOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const isParent = user?.roles?.includes("academy_parent");

  useEffect(() => {
    if (isParent) {
      fetchChildrenSessions();
    }
  }, [isParent]);

  const fetchChildrenSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/my-children-sessions`, {
        withCredentials: true,
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load session data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingOptions = async (playerId, sessionId) => {
    try {
      setLoadingOptions(true);
      const response = await axios.post(
        `${API}/academy/booking-options`,
        { player_id: playerId, session_id: sessionId },
        { withCredentials: true }
      );
      setBookingOptions(response.data);
      
      // Set default booking type based on availability
      const options = response.data.options;
      if (options.remaining_term.available) {
        setBookingType("remaining");
      } else if (options.term_with_next.available) {
        setBookingType("term_with_next");
      } else {
        setBookingType("single");
      }
    } catch (error) {
      console.error("Error fetching booking options:", error);
      setBookingOptions(null);
    } finally {
      setLoadingOptions(false);
    }
  };

  const openBookingModal = (player, session) => {
    setSelectedPlayer(player);
    setSelectedSession(session);
    setFeeCalculation(null);
    setBookingOptions(null);
    setShowBookingModal(true);
    fetchBookingOptions(player.id, session.id);
  };

  const calculateFee = async () => {
    if (!selectedPlayer || !selectedSession) return;
    
    try {
      setCalculatingFee(true);
      
      // Map frontend booking types to backend
      const backendType = bookingType === "single" ? "single" : 
                          bookingType === "full" ? "full" : 
                          bookingType === "term_with_next" ? "term_with_next" : "term";
      
      const response = await axios.post(
        `${API}/academy/calculate-upgrade-fee`,
        {
          player_id: selectedPlayer.id,
          session_id: selectedSession.id,
          booking_type: backendType,
        },
        { withCredentials: true }
      );
      // Add the original booking_type to the response for UI display
      setFeeCalculation({ ...response.data, booking_type: bookingType });
    } catch (error) {
      console.error("Error calculating fee:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate fee");
      setFeeCalculation(null);
    } finally {
      setCalculatingFee(false);
    }
  };

  useEffect(() => {
    if (showBookingModal && selectedPlayer && selectedSession && bookingOptions) {
      calculateFee();
    }
  }, [bookingType, showBookingModal, bookingOptions]);

  const createBooking = async () => {
    if (!feeCalculation) return;
    
    try {
      setCreatingBooking(true);
      
      // Map frontend booking types to backend
      const backendType = bookingType === "single" ? "single" : 
                          bookingType === "term_with_next" ? "term_with_next" : "term";
      
      const payload = {
        player_id: selectedPlayer.id,
        session_id: selectedSession.id,
        booking_type: backendType,
        booking_option: bookingType, // Store the original option for reference
        total_amount: feeCalculation.total_amount,
      };
      
      // Add next term info if it's a bundle booking
      if (bookingType === "term_with_next" && feeCalculation.next_term) {
        payload.next_term_id = feeCalculation.next_term.id;
      }
      
      const response = await axios.post(
        `${API}/academy/adhoc-booking`,
        payload,
        { withCredentials: true }
      );
      
      toast.success("Booking created! Redirecting to payment...");
      setShowBookingModal(false);
      
      // Navigate to payment page
      const bookingId = response.data.booking_id;
      navigate(`/academy/payment/${bookingId}`);
      
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setCreatingBooking(false);
    }
  };

  const getDayName = (dayNum) => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days[dayNum] || dayNum;
  };

  if (!isParent) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-6">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Parent Access Only</h2>
            <p className="text-slate-400 mb-4">This page is only available for academy parents.</p>
            <Link to="/academy">
              <Button>Back to Academy</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <div className="bg-slate-950 border-b border-white/5 text-white py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/academy">
            <Button variant="ghost" className="text-white hover:bg-slate-900/50/20 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Academy
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Session Management</h1>
          <p className="text-blue-100 mt-2">
            Add sessions or upgrade your child&apos;s enrollment
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Term Info */}
        {data?.term && (
          <Card className="mb-6 bg-indigo-500/10 border-indigo-500/30">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-indigo-300">{data.term.name}</h3>
                  <p className="text-sm text-indigo-400">
                    Week {data.term.current_week + 1} of {data.term.total_weeks}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-400">{data.term.remaining_weeks}</p>
                    <p className="text-xs text-indigo-500">Weeks Left</p>
                  </div>
                  <div className="h-10 w-px bg-indigo-200" />
                  <Badge className="bg-indigo-600">
                    Pro-rata Available
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No active term */}
        {!data?.term && (
          <Card className="mb-6 bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="py-4 text-center">
              <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
              <p className="text-yellow-400">No active term found. Session upgrades are not available at this time.</p>
            </CardContent>
          </Card>
        )}

        {/* Children's Sessions */}
        {data?.children?.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-300 mb-2">No Children Found</h3>
              <p className="text-slate-400">Please complete academy registration first.</p>
              <Link to="/academy/registration">
                <Button className="mt-4">Register for Academy</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {data?.children?.map((child, idx) => {
          // Handle both cases: when there's an active term (child has player property)
          // and when there's no active term (child IS the player object directly)
          const player = child.player || child;
          const playerName = player.name || player.child_name || `${player.given_name || ''} ${player.last_name || ''}`.trim();
          const playerId = player.id;
          
          return (
          <Card key={playerId} className="mb-6">
            <CardHeader className="border-b bg-[#0a0f1a]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <CardTitle>{playerName}</CardTitle>
                  <CardDescription>
                    Age Group: {player.age_group || "Not assigned"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Enrolled Sessions */}
              <div className="mb-6">
                <h4 className="font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  Enrolled Sessions ({child.enrolled_sessions?.length || 0})
                </h4>
                {!child.enrolled_sessions || child.enrolled_sessions.length === 0 ? (
                  <p className="text-slate-400 text-sm ml-7">No sessions enrolled yet</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3 ml-7">
                    {child.enrolled_sessions.map((session) => (
                      <div
                        key={session.id}
                        className="bg-emerald-500/10 border border-green-500/30 rounded-lg p-4"
                      >
                        {/* Session Name or Description */}
                        <p className="font-medium text-green-300">
                          {session.name || session.description || `${getDayName(session.day_of_week)} Session`}
                        </p>
                        
                        {/* Gender Badge */}
                        {session.gender && (
                          <Badge className={`mt-1 ${session.gender === 'male' ? 'bg-blue-500/20 text-blue-400' : session.gender === 'female' ? 'bg-pink-500/20 text-pink-400' : 'bg-slate-800/50 text-white'}`}>
                            {session.gender === 'male' ? '👦 Boys' : session.gender === 'female' ? '👧 Girls' : 'Mixed'}
                          </Badge>
                        )}
                        
                        {/* Day, Time */}
                        <div className="flex flex-wrap gap-2 mt-2 text-sm text-emerald-400">
                          <span className="flex items-center gap-1 bg-emerald-500/20 px-2 py-0.5 rounded">
                            <Calendar className="w-3 h-3" />
                            {getDayName(session.day_of_week)}
                          </span>
                          <span className="flex items-center gap-1 bg-emerald-500/20 px-2 py-0.5 rounded">
                            <Clock className="w-3 h-3" />
                            {session.start_time} - {session.end_time}
                          </span>
                        </div>
                        
                        {/* Location */}
                        {session.location && (
                          <p className="flex items-center gap-1 mt-2 text-sm text-slate-400">
                            <MapPin className="w-3 h-3" />
                            {session.location}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Pending Bookings */}
              {child.pending_bookings?.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-yellow-500 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Pending Bookings ({child.pending_bookings.length})
                  </h4>
                  <div className="space-y-2 ml-7">
                    {child.pending_bookings.map((booking) => (
                      <div
                        key={booking.id}
                        className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex justify-between items-center cursor-pointer hover:bg-yellow-500/20 transition-colors"
                        onClick={() => navigate(`/academy/payment/${booking.id}`)}
                      >
                        <div>
                          <p className="font-medium text-yellow-300">{booking.session_name || "Session Booking"}</p>
                          <p className="text-sm text-yellow-500">
                            {booking.booking_type === "term_upgrade" ? "Term Upgrade" : "Single Session"} - ${booking.total_amount?.toFixed(2)}
                          </p>
                        </div>
                        <Button size="sm" className="bg-yellow-500 hover:bg-yellow-400 text-black">
                          Pay Now
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Available Sessions for Upgrade */}
              {data?.term && (
                <div>
                  <h4 className="font-semibold text-blue-400 mb-3 flex items-center gap-2">
                    <Plus className="w-5 h-5" />
                    Available Sessions to Add ({child.available_sessions?.length || 0})
                  </h4>
                  {!child.available_sessions || child.available_sessions.length === 0 ? (
                    <p className="text-slate-400 text-sm ml-7">All available sessions are already enrolled</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-3 ml-7">
                      {child.available_sessions.map((session) => (
                        <div
                          key={session.id}
                          className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex-1">
                              {/* Session Name or Description */}
                              <p className="font-medium text-blue-300">
                                {session.name || session.description || `${getDayName(session.day_of_week)} Session`}
                              </p>
                              
                              {/* Gender Badge */}
                              {session.gender && (
                                <Badge className={`mt-1 ${session.gender === 'male' ? 'bg-blue-500/20 text-blue-400' : session.gender === 'female' ? 'bg-pink-500/20 text-pink-400' : 'bg-slate-800/50 text-white'}`}>
                                  {session.gender === 'male' ? '👦 Boys' : session.gender === 'female' ? '👧 Girls' : 'Mixed'}
                                </Badge>
                              )}
                              
                              {/* Age Group */}
                              {session.age_group_name && (
                                <Badge variant="outline" className="ml-1 mt-1">
                                  {session.age_group_name}
                                </Badge>
                              )}
                              
                              {/* Day, Time, Location */}
                              <div className="flex flex-wrap gap-2 mt-2 text-sm text-blue-400">
                                <span className="flex items-center gap-1 bg-blue-500/20 px-2 py-0.5 rounded">
                                  <Calendar className="w-3 h-3" />
                                  {getDayName(session.day_of_week)}
                                </span>
                                <span className="flex items-center gap-1 bg-blue-500/20 px-2 py-0.5 rounded">
                                  <Clock className="w-3 h-3" />
                                  {session.start_time} - {session.end_time}
                                </span>
                              </div>
                              
                              {/* Location */}
                              {session.location && (
                                <p className="flex items-center gap-1 mt-2 text-sm text-slate-400">
                                  <MapPin className="w-3 h-3" />
                                  {session.location}
                                </p>
                              )}
                              
                              {/* Session Type */}
                              {session.session_type && (
                                <p className="text-xs text-slate-400 mt-1 capitalize">
                                  Type: {session.session_type}
                                </p>
                              )}
                              
                              {/* Price - This is per session price */}
                              {session.price && (
                                <p className="text-sm mt-2 text-blue-400 font-bold">
                                  ${session.price}/session
                                </p>
                              )}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => openBookingModal(player, session)}
                              className="bg-blue-600 hover:bg-blue-700 shrink-0"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          );
        })}
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Session</DialogTitle>
            <DialogDescription>
              Choose how to add {selectedSession?.name || selectedSession?.description} for {selectedPlayer?.name || selectedPlayer?.child_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Loading booking options */}
            {loadingOptions ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-400" />
                <p className="text-sm text-slate-400 mt-2">Loading options...</p>
              </div>
            ) : (
            <>
            {/* Term Progress Info */}
            {bookingOptions && (
              <div className="bg-slate-800/50 rounded-lg p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Term Progress</span>
                  <span className="text-white">
                    Week {bookingOptions.current_term.current_week} of {bookingOptions.current_term.total_weeks}
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-slate-400">Remaining</span>
                  <span className="text-blue-400 font-medium">{bookingOptions.current_term.remaining_weeks} weeks</span>
                </div>
              </div>
            )}
            
            {/* Booking Type Selection */}
            <div>
              <Label className="text-sm font-medium mb-3 block">Select Booking Option</Label>
              <RadioGroup value={bookingType} onValueChange={setBookingType}>
                <div className="space-y-3">
                  {/* Single Session Option - Always available */}
                  <div className={`flex items-start space-x-3 p-3 rounded-lg border ${bookingType === "single" ? "border-purple-500 bg-purple-500/10" : "border-white/5"}`}>
                    <RadioGroupItem value="single" id="single" className="mt-1" />
                    <Label htmlFor="single" className="cursor-pointer flex-1">
                      <div className="font-medium text-purple-300">🎯 Single Session</div>
                      <div className="text-sm text-slate-400">
                        One-off session
                      </div>
                    </Label>
                  </div>
                  
                  {/* Remaining Term Option (Pro-rata) - Conditional */}
                  {bookingOptions?.options.remaining_term.available ? (
                    <div className={`flex items-start space-x-3 p-3 rounded-lg border ${bookingType === "remaining" ? "border-blue-500 bg-blue-500/10" : "border-white/5"}`}>
                      <RadioGroupItem value="remaining" id="remaining" className="mt-1" />
                      <Label htmlFor="remaining" className="cursor-pointer flex-1">
                        <div className="font-medium text-blue-300">📅 Remaining Term</div>
                        <div className="text-sm text-slate-400">
                          Pro-rate for {bookingOptions?.current_term.remaining_weeks || 0} remaining weeks
                        </div>
                      </Label>
                    </div>
                  ) : bookingOptions && (
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-white/5 opacity-50">
                      <RadioGroupItem value="remaining" id="remaining" className="mt-1" disabled />
                      <Label htmlFor="remaining" className="cursor-not-allowed flex-1">
                        <div className="font-medium text-slate-500">📅 Remaining Term</div>
                        <div className="text-sm text-slate-600">
                          {bookingOptions.options.remaining_term.unavailable_reason}
                        </div>
                      </Label>
                    </div>
                  )}
                  
                  {/* Term + Next Term Bundle Option - NEW */}
                  {bookingOptions?.options.term_with_next.available && (
                    <div className={`flex items-start space-x-3 p-3 rounded-lg border ${bookingType === "term_with_next" ? "border-amber-500 bg-amber-500/10" : "border-amber-500/30 bg-amber-500/5"}`}>
                      <RadioGroupItem value="term_with_next" id="term_with_next" className="mt-1" />
                      <Label htmlFor="term_with_next" className="cursor-pointer flex-1">
                        <div className="font-medium text-amber-300">🎁 Bundle: This Term + Next Term</div>
                        <div className="text-sm text-slate-400">
                          {bookingOptions.current_term.remaining_weeks} weeks now + {bookingOptions.options.term_with_next.next_term?.name}
                        </div>
                        <div className="text-xs text-emerald-400 mt-1">
                          ✓ Get term rate for remaining weeks!
                        </div>
                      </Label>
                    </div>
                  )}
                  
                  {/* Full Term Option */}
                  <div className={`flex items-start space-x-3 p-3 rounded-lg border ${bookingType === "full" ? "border-green-500 bg-emerald-500/10" : "border-white/5"}`}>
                    <RadioGroupItem value="full" id="full" className="mt-1" />
                    <Label htmlFor="full" className="cursor-pointer flex-1">
                      <div className="font-medium text-green-300">✨ Full Term</div>
                      <div className="text-sm text-slate-400">
                        Pay for all {bookingOptions?.current_term.total_weeks || data?.term?.total_weeks || 0} weeks
                      </div>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>
            </>
            )}

            {/* Fee Calculation */}
            {calculatingFee ? (
              <div className="text-center py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-400" />
                <p className="text-sm text-slate-400 mt-2">Calculating fee...</p>
              </div>
            ) : feeCalculation && (
              <div className="bg-[#0a0f1a] rounded-lg p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Session</span>
                  <span className="font-medium">{feeCalculation.session_name}</span>
                </div>
                
                {/* Term-based pricing display */}
                {(feeCalculation.booking_type === "remaining" || feeCalculation.booking_type === "term" || feeCalculation.booking_type === "term_upgrade") && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Term Rate (per session)</span>
                      <span>${feeCalculation.session_price?.toFixed(2) || feeCalculation.new_price_per_session?.toFixed(2) || selectedSession?.price?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm text-blue-400">
                      <span>Remaining Weeks</span>
                      <span>{feeCalculation.remaining_weeks} of {feeCalculation.total_weeks}</span>
                    </div>
                    {feeCalculation.volume_tier_applied && (
                      <div className="flex justify-between text-sm text-emerald-400">
                        <span>Volume Tier</span>
                        <span>{feeCalculation.volume_tier_applied}</span>
                      </div>
                    )}
                  </>
                )}
                
                {/* Full term pricing display */}
                {feeCalculation.booking_type === "full" && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Term Rate (per session)</span>
                      <span>${feeCalculation.session_price?.toFixed(2) || selectedSession?.price?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm text-emerald-400">
                      <span>Full Term</span>
                      <span>{feeCalculation.total_weeks} weeks</span>
                    </div>
                  </>
                )}
                
                {/* Single session pricing display - clean without premium details */}
                {feeCalculation.booking_type === "single" && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Single Session Price</span>
                      <span className="font-medium">${feeCalculation.total_amount?.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between text-sm text-purple-400">
                      <span>Sessions</span>
                      <span>1 session</span>
                    </div>
                    
                    {feeCalculation.has_term_pass && (
                      <div className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                        ✓ Term pass holder rate applied
                      </div>
                    )}
                    
                    {!feeCalculation.has_term_pass && (
                      <div className="text-xs text-blue-400/70 bg-blue-500/10 px-2 py-1 rounded mt-2">
                        💡 Tip: Enroll in a term for better value per session!
                      </div>
                    )}
                  </>
                )}
                
                {/* Bundle: Term + Next Term display */}
                {feeCalculation.booking_type === "term_with_next" && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Term Rate (per session)</span>
                      <span>${feeCalculation.session_price?.toFixed(2) || '0.00'}</span>
                    </div>
                    
                    <div className="border-t border-slate-700 pt-2 mt-2 space-y-2">
                      <div className="text-xs text-slate-400 uppercase font-medium">Current Term</div>
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-300">{feeCalculation.current_term?.name}</span>
                        <span>{feeCalculation.current_term?.remaining_weeks} weeks × ${feeCalculation.session_price?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-slate-400">
                        <span>Subtotal</span>
                        <span>${feeCalculation.current_term?.amount?.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="border-t border-slate-700 pt-2 mt-2 space-y-2">
                      <div className="text-xs text-slate-400 uppercase font-medium">Next Term</div>
                      <div className="flex justify-between text-sm">
                        <span className="text-amber-300">{feeCalculation.next_term?.name}</span>
                        <span>{feeCalculation.next_term?.weeks} weeks × ${feeCalculation.session_price?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-slate-400">
                        <span>Subtotal</span>
                        <span>${feeCalculation.next_term?.amount?.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    {feeCalculation.savings_note && (
                      <div className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded mt-2">
                        ✓ {feeCalculation.savings_note}
                      </div>
                    )}
                  </>
                )}
                
                {feeCalculation.membership_discount && feeCalculation.membership_discount !== "0%" && feeCalculation.booking_type !== "single" && (
                  <div className="flex justify-between text-sm text-emerald-400">
                    <span>Membership Discount</span>
                    <span>-{feeCalculation.membership_discount}</span>
                  </div>
                )}
                
                <div className="border-t pt-3 flex justify-between font-bold">
                  <span>Total Amount</span>
                  <span className="text-lg text-blue-400">${feeCalculation.total_amount?.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={createBooking}
              disabled={!feeCalculation || creatingBooking}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {creatingBooking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Proceed to Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
